import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getApi, postApi } from "../../utils/api";
import {
  UPLOAD_USER_PHOTO,
  USER_PROFILE,
  API_GET_EMPLOYEE_FILE,
} from "../../config/Endpoints";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import Salary from "./Salary";
import Form16 from "./Form16";
import WorkExperience from "./WorkExperience";
import EmployeeAsset from "./EmployeeAsset";
import OrgChartExample from "../OrgChartExample";
import IndexChart from "../org-diagram";
import Education from "./Education";
import Dependents from "./Dependent";
import { logoutUser } from "../../utils/Storage";
import ChangePassword from "./ChangePassword";
import { toggleMenu } from "../../utils/helpers";
import DocumentList from "./DocumentList";
import AppraisalList from "./AppraisalList";
import Footer from "../../components/Footer";
import axios from "axios";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { employeeTypeOptions } from "../../options/employeeTypeOptions";

const AdminProfile = () => {
  const [userData, setUserData] = useState({});
  const accessToken = Cookies.get("accessToken");
  const [profileImage, setProfileImage] = useState("../assets/profile.png");
  const [croppedBlob, setCroppedBlob] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [minZoom, setMinZoom] = useState(1);
  const [showCropModal, setShowCropModal] = useState(false);
  const [upImg, setUpImg] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const employeedata = JSON.parse(localStorage.getItem("userData"));
  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };
  const onMediaLoaded = (mediaSize) => {
    const { width, height } = mediaSize;
    const cropSize = 200; // cropSize width/height
    // Calculate minimum zoom to fill the crop area
    const minZoomValue = Math.max(cropSize / width, cropSize / height);
    setMinZoom(minZoomValue);
  };

  useEffect(() => {
    if (showCropModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showCropModal]);

  const handleCropSave = async () => {
    let blobToUpload = croppedBlob;

    // If not in preview mode, create blob from current crop
    if (!blobToUpload && upImg && croppedAreaPixels) {
      const image = await createImage(upImg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      blobToUpload = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/jpeg", 0.95);
      });
    }

    if (!blobToUpload) {
      toast.error("Failed to process image.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Close modal first
    setShowCropModal(false);
    setIsPreviewMode(false);
    setCroppedBlob(null);

    setIsImageLoading(true);
    const formData = new FormData();
    const file = new File([blobToUpload], "profile.jpg", {
      type: "image/jpeg",
    });
    formData.append("photo", file);

    try {
      const res = await postApi(UPLOAD_USER_PHOTO, formData, accessToken);
      if (res.status === 200) {
        toast.success(res.data.message, "Profile Updated Successfully!", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setIsImageLoading(false);

        fetchUser(); // refresh user details
      } else {
        setIsImageLoading(false);
        toast.error(res.data.message, "Failed to update photo.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (e) {
      setIsImageLoading(false);
      toast.error("Failed to update photo.", {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        toast.error("Only JPG, JPEG, or PNG files are allowed.");
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        toast.error("Photo size exceeds the maximum limit of 2MB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          // Image loaded successfully
          setUpImg(reader.result);
          setShowCropModal(true);
        };
        img.onerror = () => {
          // Image is corrupted or invalid
          toast.error("The selected photo appears to be corrupted or invalid.");
        };
        img.src = reader.result;
      };

      reader.readAsDataURL(file);

      setPreviewImage(null);
      setCroppedBlob(null);
      setIsPreviewMode(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setMinZoom(1);
      setCroppedAreaPixels(null);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.value = null;
    setUpImg(null);
    setPreviewImage(null);
    setCroppedBlob(null);
    setIsPreviewMode(false);
    fileInputRef.current.click();
  };

  const handlePreviewCrop = async () => {
    if (!upImg || !croppedAreaPixels) return;

    const image = await createImage(upImg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    const base64Image = canvas.toDataURL("image/jpeg");
    setPreviewImage(base64Image);

    canvas.toBlob(
      (blob) => {
        setCroppedBlob(blob);
      },
      "image/jpeg",
      0.95
    );

    setIsPreviewMode(true);
  };

  useEffect(() => {
    if (accessToken) {
      fetchUser();
    }
  }, []);

  const fetchUser = () => {
    setIsLoading(true);

    getApi(USER_PROFILE, accessToken)
      .then(async (res) => {
        if (res.status === 200) {
          const user = res.data.data;
          setUserData(user);

          const photoPath = user?.personalDetail?.photoPath;
          setIsImageLoading(true);

          if (photoPath?.includes("photoDir")) {
            const parts = photoPath.split("/");
            const result = parts.slice(3).join("/");

            try {
              const imgRes = await axios.get(API_GET_EMPLOYEE_FILE, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { filepath: result },
                responseType: "blob",
              });

              if (imgRes.status === 200) {
                const imageUrl = URL.createObjectURL(imgRes.data);
                setProfileImage(imageUrl);
              } else {
                setProfileImage("../assets/profile.png");
              }
            } catch (err) {
              console.error("Error fetching image:", err);
              setProfileImage("../assets/profile.png");
            }
          } else {
            setProfileImage("../assets/profile.png");
          }
          setIsImageLoading(false);
        }
      })
      .catch((e) => console.error("Profile fetch error:", e))
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="wrapper-body">
      <div className="page-heading">
        <div id="showMenuBtn" className="collapse-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      {isLoading && (
        <div className="centered-loader">
          <ThreeDots
            height="100"
            width="100"
            radius="9"
            color="blue"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClassName=""
            visible={true}
          />
        </div>
      )}
      {!isLoading && (
        // <form id="assign-asset">
        <div className="card card-radius">
          <div className="row">
            <div className="col-md-2 border-right">
              <div className="d-flex flex-column align-items-center text-center p-3 py-2 mt-2">
                {isImageLoading && (
                  <div className="mt-5">
                    <TailSpin
                      height="25"
                      width="80"
                      radius="9"
                      color="blue"
                      ariaLabel="tail-spin-loading"
                      visible={true}
                    />
                  </div>
                )}

                {!isImageLoading && (
                  <img
                    className="employee-profile"
                    width="150px"
                    height="150px"
                    alt="employee-Profile-img"
                    src={profileImage}
                  />
                )}
              </div>
            </div>

            <div className="col-md-8">
              <div className="d-flex flex-column p-1 py-3">
                <h4>
                  <b>{userData?.personalDetail?.empName}</b>
                </h4>

                <span className="text-black font-small">
                  {userData?.personalDetail?.designation || ""}
                </span>

                {userData?.personalDetail?.ccEmail && (
                  <span className="text-black font-small">
                    <a href={`mailto:${userData?.personalDetail?.ccEmail}`}>
                      <i
                        className="fa fa-envelope-o"
                        aria-hidden="true"
                        style={{ fontSize: "12px" }}
                      ></i>
                      <span className="ms-1">
                        {userData?.personalDetail?.ccEmail}
                      </span>
                    </a>
                  </span>
                )}

                {userData?.personalDetail?.phoneNumber && (
                  <span className="text-black font-small mb-2">
                    <a
                      href={`tel:${
                        employeedata?.location === "CCI_INDIA" ||
                        employeedata?.location === "IRED_INDIA"
                          ? `+91${userData.personalDetail.phoneNumber}`
                          : `+1${userData.personalDetail.phoneNumber}`
                      }`}
                    >
                      <i
                        className="fa fa-mobile"
                        aria-hidden="true"
                        style={{ fontSize: "16px" }}
                      ></i>
                      <span className="ms-1">
                        {employeedata?.location === "CCI_INDIA" ||
                        employeedata?.location === "IRED_INDIA"
                          ? `+91 ${userData.personalDetail.phoneNumber.replace(
                              /(\d{5})(\d{5})/,
                              "$1-$2"
                            )}`
                          : `+1 (${userData.personalDetail.phoneNumber.slice(
                              0,
                              3
                            )}) ${userData.personalDetail.phoneNumber.slice(
                              3,
                              6
                            )}-${userData.personalDetail.phoneNumber.slice(
                              6,
                              10
                            )}`}
                      </span>
                    </a>
                  </span>
                )}

                <div className="d-flex mt-2">
                  <button
                    className=" update-photo-btn btn theme-background-color btn-sm me-2"
                    onClick={handleImageClick}
                  >
                    Update Photo
                  </button>

                  <button
                    className="btn change-password-btn  btn-sm theme-color-outline theme-color"
                    data-bs-toggle="modal"
                    data-bs-target="#staticBackdropChangePassword"
                  >
                    Change Password
                  </button>
                </div>

                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={onSelectFile}
                />
              </div>
            </div>

            <div className="col-md-2"></div>
          </div>

          <div className="row mt-5">
            <div className="container">
              <ul className="nav nav-tabs" id="myTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="tab1-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab1"
                    type="button"
                    role="tab"
                    aria-controls="tab1"
                    aria-selected="true"
                  >
                    General Information
                  </button>
                </li>
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab2-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab2"
                      type="button"
                      role="tab"
                      aria-controls="tab2"
                      aria-selected="false"
                    >
                      Job
                    </button>
                  </li> */}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab3-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab3"
                      type="button"
                      role="tab"
                      aria-controls="tab3"
                      aria-selected="false"
                    >
                      Leave
                    </button>
                  </li> */}
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab4-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab4"
                    type="button"
                    role="tab"
                    aria-controls="tab4"
                    aria-selected="false"
                  >
                    Assigned Assets
                  </button>
                </li>
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab5-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab5"
                      type="button"
                      role="tab"
                      aria-controls="tab5"
                      aria-selected="false"
                    >
                      Performance
                    </button>
                  </li> */}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab6-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab6"
                      type="button"
                      role="tab"
                      aria-controls="tab6"
                      aria-selected="false"
                    >
                      Permission
                    </button>
                  </li> */}
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab7-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab7"
                      type="button"
                      role="tab"
                      aria-controls="tab7"
                      aria-selected="false"
                    >
                      Documents
                    </button>
                  </li> */}
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab8-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab8"
                    type="button"
                    role="tab"
                    aria-controls="tab8"
                    aria-selected="false"
                  >
                    Salary Records
                  </button>
                </li>
                {/* <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="tab9-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#tab9"
                      type="button"
                      role="tab"
                      aria-controls="tab9"
                      aria-selected="false"
                    >
                      Organization Chart
                    </button>
                  </li> */}
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab10-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab10"
                    type="button"
                    role="tab"
                    aria-controls="tab10"
                    aria-selected="false"
                  >
                    Uploaded Documents
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab11-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab11"
                    type="button"
                    role="tab"
                    aria-controls="tab11"
                    aria-selected="false"
                  >
                    Appraisal History
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="tab12-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#tab12"
                    type="button"
                    role="tab"
                    aria-controls="tab12"
                    aria-selected="false"
                  >
                    Tax Documents
                  </button>
                </li>
              </ul>

              <div
                className="tab-content admin-profile-tab-content"
                id="myTabsContent"
              >
                <div
                  className="tab-pane fade show active mt-3"
                  id="tab1"
                  role="tabpanel"
                  aria-labelledby="tab1-tab"
                >
                  <div className="accordion" id="accordion1">
                    <div className="accordion-item rounded-lg-top">
                      <h2 className="accordion-header " id="heading1">
                        <button
                          className="accordion-button collapsed rounded-lg-top"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse1"
                          aria-expanded="false"
                          aria-controls="collapse1"
                        >
                          Basic info
                        </button>
                      </h2>
                      <div
                        id="collapse1"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading1"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row mb-1 ">
                            <div className="col-md-3 text-black-50">
                              First Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.firstName
                                ? userData.personalDetail.firstName
                                    .charAt(0)
                                    .toUpperCase() +
                                  userData.personalDetail.firstName
                                    .slice(1)
                                    .toLowerCase()
                                : "-"}
                            </div>
                            <div className="col-md-3 text-black-50">
                              Middle Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.middleName
                                ? userData.personalDetail.middleName
                                    .charAt(0)
                                    .toUpperCase() +
                                  userData.personalDetail.middleName
                                    .slice(1)
                                    .toLowerCase()
                                : "-"}
                            </div>
                          </div>

                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Last Name
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.lastName
                                ? userData.personalDetail.lastName
                                    .charAt(0)
                                    .toUpperCase() +
                                  userData.personalDetail.lastName
                                    .slice(1)
                                    .toLowerCase()
                                : "-"}
                            </div>
                            <div className="col-md-3 text-black-50">Email</div>
                            <div className="col-md-3 mb-2 text-break">
                              {userData?.personalDetail?.ccEmail}
                            </div>
                          </div>

                          <div className="row mb-1">
                            <div className="col-md-3 text-black-50">
                              Employee Id
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.employeeId}
                            </div>
                            <div className="col-md-3 text-black-50">
                              Employee Flag
                            </div>
                            <div className="col-md-3 mb-2">
                              {userData?.personalDetail?.location}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading2">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse2"
                          aria-expanded="false"
                          aria-controls="collapse2"
                        >
                          Work
                        </button>
                      </h2>
                      <div
                        id="collapse2"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading2"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-3">
                              <div className="d-flex flex-column  text-black-50">
                                <p>Department</p>
                                <p>Designation</p>
                                <p>Reporting To</p>
                                <p>Employee Status</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex flex-column">
                                <p>
                                  {userData?.personalDetail?.department
                                    ? userData?.personalDetail?.department
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.designation
                                    ? userData?.personalDetail?.designation
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.managerName
                                    ? userData?.personalDetail?.managerName
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.status
                                    ? userData?.personalDetail?.status
                                    : "-"}
                                </p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex flex-column  text-black-50">
                                <p>Date of Joining</p>
                                <p>Employee Type</p>
                                <p>Employee Sub Type</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex flex-column">
                                <p>
                                  {userData?.personalDetail?.doj
                                    ? userData?.personalDetail?.doj
                                    : "-"}
                                </p>
                                <p>
                                  {employeeTypeOptions.find(
                                    (opt) =>
                                      opt.value ===
                                      userData?.personalDetail?.employeeType
                                  )?.label || "-"}
                                </p>
                                <p>
                                  {" "}
                                  {userData?.personalDetail?.employeeSubType
                                    ? userData?.personalDetail?.employeeSubType.replace(
                                        "Fulltime",
                                        "Full Time"
                                      )
                                    : "-"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading3">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse3"
                          aria-expanded="false"
                          aria-controls="collapse3"
                        >
                          Personal Details
                        </button>
                      </h2>
                      <div
                        id="collapse3"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading3"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-3">
                              <div className="d-flex flex-column  text-black-50">
                                <p>Date of Birth</p>
                                <p>Aadhar Card Number</p>
                                <p>Passport Number</p>
                                <p>PAN Number</p>
                                <p>Present Address</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex flex-column">
                                <p>
                                  {userData?.personalDetail?.dob
                                    ? userData?.personalDetail?.dob
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.aadharNum
                                    ? userData?.personalDetail?.aadharNum
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.passportNum
                                    ? userData?.personalDetail?.passportNum
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.panNum
                                    ? userData?.personalDetail?.panNum
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.presentAddress
                                    ? userData?.personalDetail?.presentAddress
                                    : "-"}
                                </p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex flex-column  text-black-50">
                                <p>Blood Group</p>
                                <p>Gender</p>
                                <p>Marital Status</p>
                                <p>Alternate Number</p>
                                <p>Permanent Address</p>
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="d-flex flex-column">
                                <p>
                                  {userData?.personalDetail?.bloodGroup
                                    ? userData?.personalDetail?.bloodGroup
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.gender
                                    ? userData?.personalDetail?.gender
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.maritalStatus
                                    ? userData?.personalDetail?.maritalStatus
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.alternateNumber
                                    ? userData?.personalDetail?.alternateNumber
                                    : "-"}
                                </p>
                                <p>
                                  {userData?.personalDetail?.permanentAddress
                                    ? userData?.personalDetail?.permanentAddress
                                    : "-"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading4">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse4"
                          aria-expanded="false"
                          aria-controls="collapse4"
                        >
                          Work Experience
                        </button>
                      </h2>
                      <div
                        id="collapse4"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading4"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-12">
                              <WorkExperience workData={userData?.experience} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="heading5">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse5"
                          aria-expanded="false"
                          aria-controls="collapse5"
                        >
                          Education
                        </button>
                      </h2>
                      <div
                        id="collapse5"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading5"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-12">
                              <Education educationData={userData?.education} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="accordion-item  rounded-lg-bottom">
                      <h2 className="accordion-header" id="heading6">
                        <button
                          className="accordion-button collapsed rounded-lg-bottom"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse6"
                          aria-expanded="false"
                          aria-controls="collapse6"
                        >
                          Dependents
                        </button>
                      </h2>
                      <div
                        id="collapse6"
                        className="accordion-collapse collapse"
                        aria-labelledby="heading6"
                        data-bs-parent="#accordion1"
                      >
                        <div className="accordion-body">
                          <div className="row">
                            <div className="col-md-12">
                              <Dependents
                                dependentData={userData?.dependents}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="tab-pane fade"
                  id="tab2"
                  role="tabpanel"
                  aria-labelledby="tab2-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab3"
                  role="tabpanel"
                  aria-labelledby="tab3-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab4"
                  role="tabpanel"
                  aria-labelledby="tab4-tab"
                >
                  <EmployeeAsset assestData={userData?.assestDetail} />
                </div>

                <div
                  className="tab-pane fade"
                  id="tab5"
                  role="tabpanel"
                  aria-labelledby="tab5-tab"
                >
                  <OrgChartExample />
                </div>

                <div
                  className="tab-pane fade"
                  id="tab6"
                  role="tabpanel"
                  aria-labelledby="tab6-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab7"
                  role="tabpanel"
                  aria-labelledby="tab7-tab"
                ></div>

                <div
                  className="tab-pane fade"
                  id="tab8"
                  role="tabpanel"
                  aria-labelledby="tab8-tab"
                >
                  <Salary />
                </div>
                {/* <div
                  className="tab-pane fade"
                  id="tab9"
                  role="tabpanel"
                  aria-labelledby="tab9-tab"
                >
                  <IndexChart />
                </div> */}
                <div
                  className="tab-pane fade"
                  id="tab10"
                  role="tabpanel"
                  aria-labelledby="tab10-tab"
                >
                  <DocumentList />
                </div>
                <div
                  className="tab-pane fade"
                  id="tab11"
                  role="tabpanel"
                  aria-labelledby="tab11-tab"
                >
                  <AppraisalList />
                </div>

                <div
                  className="tab-pane fade"
                  id="tab12"
                  role="tabpanel"
                  aria-labelledby="tab12-tab"
                >
                  <Form16 />
                </div>
              </div>
            </div>
          </div>
        </div>
        // </form>
      )}
      <div
        className="modal fade"
        id="staticBackdropChangePassword"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <ChangePassword />
          </div>
        </div>
      </div>
      <Footer />

      {showCropModal && <div className="modal-backdrop fade show"></div>}
      <div
        className={`modal fade ${showCropModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content p-3">
            <div className="modal-header p-0 pb-2">
              <h5 className="modal-title">Crop Photo</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowCropModal(false)}
              ></button>
            </div>
            <div className="modal-body text-center">
              {isPreviewMode ? (
                <img
                  src={previewImage}
                  alt="Preview"
                  className="rounded-circle preview-image"
                  width="200"
                  height="200"
                />
              ) : (
                upImg && (
                  <div
                    style={{ position: "relative", width: "100%", height: 400 }}
                  >
                    <Cropper
                      image={upImg}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      onMediaLoaded={onMediaLoaded}
                      cropSize={{ width: 200, height: 200 }}
                      minZoom={minZoom}
                      maxZoom={3}
                    />
                  </div>
                )
              )}
            </div>

            <div className="modal-footer gap-2">
              {isPreviewMode ? (
                <>
                  <button
                    className=" theme-button bg-grey"
                    onClick={() => setIsPreviewMode(false)}
                  >
                    Back
                  </button>
                  <button className=" theme-button" onClick={handleCropSave}>
                    Update
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn theme-button"
                    onClick={handlePreviewCrop}
                  >
                    Preview
                  </button>
                  <button className="btn theme-button" onClick={handleCropSave}>
                    Update
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
